import React, { memo, useState, useEffect, useRef } from 'react';
import cn from 'classnames';
import { Trans, useTranslation } from 'next-i18next';
import { debounce } from 'lodash';

import { Spinner } from 'components';
import SelectionTickMark from 'components/form/SelectionTickMark';
import { Row } from 'components/layout';
import useScrollOffset from 'hooks/useScrollOffset';
import { useVisibleOnIntersect } from 'hooks/useVisibleOnIntersect';
import { useResponsive } from 'hooks/useResponsive';
import { useNotification } from 'hooks/useNotification';
import { useCandidate } from 'hooks/useCandidate';
import { useCandidateOnboarding } from 'hooks/useCandidateOnboarding';
import { Option } from 'lib/models/option';
import { MATCHING_PREFERENCE_STEPS } from 'lib/models/candidate';
import { Footer } from '../Footer/Footer.component';
import { SidebarMP } from '../SidebarMP/SidebarMP.component';
import MobileHeaderMp from '../MobileHeaderMp';

import styles from '../Onboarding.module.scss';

export interface EventTypeProps {
  /** Custom class name */
  className?: string;
  candidateSelectedIds: Array<string> | null;
  onNextClick: (eventSelected: Array<string>, isBackClicked?: boolean) => void;
  isLoading: boolean;
  isBackLoading: boolean;
  eventKinds: Array<Option> | undefined;
  onStepSelect?: (step: MATCHING_PREFERENCE_STEPS) => void;
}

export const EventType: React.FC<EventTypeProps> = memo(
  ({
    className = '', // custom class name
    candidateSelectedIds,
    onNextClick,
    isLoading,
    isBackLoading,
    eventKinds,
    onStepSelect,
  }: EventTypeProps) => {
    const [t] = useTranslation('candidate-onboarding');
    const bottomRef = useRef<HTMLDivElement>(null);
    const screens = useResponsive();
    const notificationInstance = useNotification();
    const { updateCandidateInfo } = useCandidate();
    const { isValueEdited, setValueEdited, onBackClickMPMobile } =
      useCandidateOnboarding();

    const { isWindowScrolled } = useScrollOffset();

    const contentClassNames = cn(styles.container, className);

    const [selectedIds, setSelectedIds] = useState<Array<string>>([]);
    const [fullyScrolled, setFullyScrolled] = useState(false);

    useEffect(() => {
      if (candidateSelectedIds) {
        setSelectedIds(candidateSelectedIds);
      }
    }, [candidateSelectedIds]);
    const onChevronClick = () => {
      if (isValueEdited) {
        onBackClickMPMobile();
      } else {
        onSubmit(false);
      }
    };
    const onSubmit = (isBackClicked?: boolean) => {
      setValueEdited(false);
      onNextClick(selectedIds, isBackClicked);
    };
    useVisibleOnIntersect({
      elRef: bottomRef,
      threshold: 0,
      onIntersect: () => setFullyScrolled(true),
      onHidden: () => setFullyScrolled(false),
      rootMargin: '0px',
    });

    const apiCallToUpdate = async (modifiedEventType: Array<string>) => {
      try {
        await updateCandidateInfo({
          partialCandidateAttributes: {
            event_category_ids: modifiedEventType,
          },
        });
      } catch (error) {
        notificationInstance.handleExceptionError(error);
      }
    };

    const debouncedAPICall = React.useRef(
      debounce(async (modifiedEventType) => {
        await apiCallToUpdate(modifiedEventType);
      }, 3000)
    ).current;

    const onEventCheckboxClick = (id: string) => {
      setValueEdited(true);

      const index = selectedIds?.findIndex((type) => type === id);
      let modifiedEventType = [];
      if (index === -1) modifiedEventType = [...selectedIds, id];
      else
        modifiedEventType =
          selectedIds && selectedIds.filter((item) => item !== id);

      /** For MP
       * Desktop: Autosave on every change
       * Mobile: Save on CTA click
       */
      setSelectedIds(modifiedEventType);

      if (!screens.sm) {
        debouncedAPICall(modifiedEventType);
      }
    };

    const isReady = selectedIds.length !== 0 ? true : false;

    if (!eventKinds) {
      return (
        <div className={styles.spinner}>
          <Spinner />
        </div>
      );
    }

    return (
      <div>
        <MobileHeaderMp
          title={t('matching-preferences.event-categories')}
          onChevronClick={onChevronClick}
        />

        <div className={contentClassNames} data-testid="EventType">
          {onStepSelect && (
            <SidebarMP className={styles.sidebar} onStepSelect={onStepSelect} />
          )}
          <div
            className={cn(
              styles.content,
              styles['content-mp'],
              styles['event-content']
            )}
          >
            <p className={styles.setup}>{t('event-type.title')}</p>
            <p className={cn(styles.desc)}>{t('event-type.subtitle')}</p>
            <Row wrap={false} className={cn(styles.descRow, styles.desc)}>
              <p className={styles.tick}>✅</p>
              <p>
                <Trans
                  t={t}
                  i18nKey="event-type.desc"
                  components={{ 1: <mark />, 2: <mark /> }}
                >
                  <mark>Careers & Employability</mark> and{' '}
                  <mark>Business & Entrepreneurship</mark> are selected by
                  default.
                </Trans>
              </p>
            </Row>
            <div className={styles.checkboxes}>
              {eventKinds?.map((item) => (
                <SelectionTickMark
                  isTickSquare={true}
                  key={item.id}
                  checked={selectedIds?.some((id) => item.id === id)}
                  onChange={() => onEventCheckboxClick(item.id)}
                  className={cn(styles.selectionbox)}
                >
                  {item.label}
                </SelectionTickMark>
              ))}
            </div>
            <div className={styles['bottom-ref']} ref={bottomRef}></div>
          </div>

          <Footer
            isBackLoading={isBackLoading}
            isLoading={isLoading}
            isReady={isReady}
            onSubmit={onSubmit}
            isFullyScrolled={fullyScrolled}
            isWindowScrolled={isWindowScrolled}
            isSkippable={true}
            isMatchingPreference={true}
          />
        </div>
      </div>
    );
  }
);

EventType.displayName = 'EventType';
